import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { loadInvoicePreviewDrafts } from "../../utilis/financeInvoiceEmailHelpers";
import InvoiceXeroPreview from "../Finance/InvoiceXeroPreview";

function ClientInvoicesPrintModal({
  show = false,
  invoiceIds = [],
  clientId = null,
  clientProfile = null,
  isCommission = false,
  onClose,
}) {
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState([]);

  useEffect(() => {
    if (!show || !invoiceIds.length) {
      setDrafts([]);
      return undefined;
    }

    let cancelled = false;

    const loadDrafts = async () => {
      setLoading(true);
      setDrafts([]);

      try {
        const prepared = await loadInvoicePreviewDrafts(invoiceIds, {
          clientId,
          clientProfile,
          isCommission,
        });
        if (cancelled) return;

        const nextDrafts = prepared.map((item) => item.draft).filter(Boolean);

        if (!nextDrafts.length) {
          await Swal.fire(
            "Error",
            "Unable to load the selected invoice(s) for preview.",
            "error",
          );
          onClose?.();
          return;
        }

        setDrafts(nextDrafts);
      } catch (error) {
        console.error("Failed to prepare invoices for preview", error);
        if (!cancelled) {
          await Swal.fire(
            "Error",
            error?.response?.data?.message ||
              error?.message ||
              "Failed to prepare invoices for preview.",
            "error",
          );
          onClose?.();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDrafts();

    return () => {
      cancelled = true;
    };
  }, [show, invoiceIds, clientId, clientProfile, isCommission, onClose]);

  if (!show) return null;

  if (loading) {
    return (
      <>
        <div
          className="modal-backdrop fade show invoice-xero-preview-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          className="invoice-xero-preview-modal"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="invoice-xero-preview">
            <div className="invoice-xero-preview__toolbar no-print">
              <div>
                <h6 className="mb-0 fw-bold">Invoice preview</h6>
                <div className="text-muted small">Loading invoice details…</div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (drafts.length === 1) {
    return (
      <>
        <div
          className="modal-backdrop fade show invoice-xero-preview-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          className="invoice-xero-preview-modal"
          onClick={(event) => event.stopPropagation()}
        >
          <InvoiceXeroPreview draft={drafts[0]} onClose={onClose} showToolbar />
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="modal-backdrop fade show invoice-xero-preview-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="invoice-xero-preview-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="invoice-xero-preview">
          <div className="invoice-xero-preview__toolbar no-print">
            <div>
              <h6 className="mb-0 fw-bold">Invoice preview</h6>
              <div className="text-muted small">
                Previewing {drafts.length} selected invoices.
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={onClose}
              >
                Close preview
              </button>
            </div>
          </div>

          <div
            className="invoice-xero-preview__canvas"
            id="invoice-xero-preview-print"
          >
            {drafts.map((draft, index) => (
              <InvoiceXeroPreview
                key={draft.invoiceNumber || `preview-draft-${index}`}
                draft={draft}
                embedded
                showToolbar={false}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default ClientInvoicesPrintModal;
